import { useEffect, useMemo, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  LayoutGrid,
  Plus,
  Search,
  Pencil,
  CircleCheck,
  CircleSlash,
  Power,
  Trash2,
  RotateCcw,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { PLANTILLAS_MESAS as T } from "@/constants/testIds";
import { getAdminSession, puedeAccederModulo } from "@/data/adminAuthStore";
import {
  getCatalogoSalones,
  listarPlantillas,
  activarPlantilla,
  desactivarPlantilla,
  eliminarPlantilla,
} from "@/data/plantillasMesasStore";

const ESTADO_OPCIONES = [
  { key: "activas", label: "Activas", activa: true },
  { key: "inactivas", label: "Inactivas", activa: false },
  { key: "todas", label: "Todas", activa: null },
];

function EstadoBadge({ activa }) {
  return (
    <span
      className={cn(
        "inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs font-medium",
        activa
          ? "text-emerald-700 dark:text-emerald-300 bg-emerald-500/10 border-emerald-500/40"
          : "text-slate-600 dark:text-slate-300 bg-slate-500/10 border-slate-500/40",
      )}
    >
      {activa ? (
        <CircleCheck className="w-3.5 h-3.5" strokeWidth={1.9} />
      ) : (
        <CircleSlash className="w-3.5 h-3.5" strokeWidth={1.9} />
      )}
      {activa ? "Activa" : "Inactiva"}
    </span>
  );
}

export default function PlantillasMesasListado() {
  const navigate = useNavigate();
  const rol = getAdminSession()?.rol || null;
  const autorizado = puedeAccederModulo(rol, "plantillasMesas");

  const [salones, setSalones] = useState([]);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [busqueda, setBusqueda] = useState("");
  const [salonFiltro, setSalonFiltro] = useState("");
  const [estadoFiltro, setEstadoFiltro] = useState("activas");
  const [porEliminar, setPorEliminar] = useState(null);

  const cargar = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const activa = ESTADO_OPCIONES.find((e) => e.key === estadoFiltro)?.activa;
      const data = await listarPlantillas({
        salon: salonFiltro || undefined,
        activa: activa === null ? undefined : activa,
      });
      setItems(Array.isArray(data) ? data : []);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [salonFiltro, estadoFiltro]);

  useEffect(() => {
    if (!autorizado) return;
    getCatalogoSalones().then(setSalones).catch(() => setSalones([]));
  }, [autorizado]);

  useEffect(() => {
    if (autorizado) cargar();
  }, [autorizado, cargar]);

  const visibles = useMemo(() => {
    const q = busqueda.trim().toLowerCase();
    if (!q) return items;
    return items.filter((p) => (p.nombre || "").toLowerCase().includes(q));
  }, [items, busqueda]);

  if (!autorizado) {
    return (
      <div
        data-testid={T.denied}
        className="min-h-[60vh] grid place-items-center text-center px-6"
      >
        <div className="max-w-md">
          <CircleSlash className="w-10 h-10 mx-auto text-muted-foreground mb-4" strokeWidth={1.5} />
          <h1 className="text-xl font-semibold mb-2">Acceso restringido</h1>
          <p className="text-sm text-muted-foreground">
            No tienes permiso para administrar plantillas de mesas.
          </p>
        </div>
      </div>
    );
  }

  const toggleActiva = async (p) => {
    try {
      if (p.activa) await desactivarPlantilla(p.id);
      else await activarPlantilla(p.id);
      toast.success(p.activa ? "Plantilla desactivada." : "Plantilla reactivada.");
      cargar();
    } catch (e) {
      toast.error(e.message);
    }
  };

  const confirmarEliminar = async () => {
    const p = porEliminar;
    setPorEliminar(null);
    try {
      await eliminarPlantilla(p.id);
      toast.success("Plantilla eliminada (baja lógica). Consulta el filtro Inactivas.");
      cargar();
    } catch (e) {
      toast.error(e.message);
    }
  };

  return (
    <div data-testid={T.screen} className="max-w-6xl mx-auto px-4 md:px-8 py-6 pb-28 space-y-6">
      <header className="flex items-center justify-between gap-4 flex-wrap">
        <div className="flex items-center gap-3">
          <LayoutGrid className="w-6 h-6 text-accent" strokeWidth={1.7} />
          <div>
            <h1 className="text-2xl font-semibold">Plantillas de mesas</h1>
            <p className="text-sm text-muted-foreground">
              Distribuciones reutilizables de mesas por salón oficial.
            </p>
          </div>
        </div>
        <button
          type="button"
          data-testid={T.btnNueva}
          onClick={() => navigate("/admin/plantillas-mesas/nueva")}
          className="inline-flex items-center justify-center gap-2 rounded-xl px-4 min-h-[44px] text-sm font-medium bg-primary text-primary-foreground hover:bg-primary/90 transition-colors"
        >
          <Plus className="w-4 h-4" strokeWidth={2} />
          Nueva plantilla
        </button>
      </header>

      {/* Filtros */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <input
            data-testid={T.buscarInput}
            value={busqueda}
            onChange={(e) => setBusqueda(e.target.value)}
            placeholder="Buscar por nombre…"
            className="w-full rounded-xl border hairline bg-background pl-9 pr-3 min-h-[44px] text-sm outline-none focus:ring-2 focus:ring-accent/40"
          />
        </div>
        <select
          data-testid={T.filtroSalon}
          value={salonFiltro}
          onChange={(e) => setSalonFiltro(e.target.value)}
          className="w-full rounded-xl border hairline bg-background px-3 min-h-[44px] text-sm outline-none focus:ring-2 focus:ring-accent/40"
        >
          <option value="">Todos los salones</option>
          {salones.map((s) => (
            <option key={s.nombre} value={s.nombre}>{s.nombre}</option>
          ))}
        </select>
        <select
          data-testid={T.filtroEstado}
          value={estadoFiltro}
          onChange={(e) => setEstadoFiltro(e.target.value)}
          className="w-full rounded-xl border hairline bg-background px-3 min-h-[44px] text-sm outline-none focus:ring-2 focus:ring-accent/40"
        >
          {ESTADO_OPCIONES.map((o) => (
            <option key={o.key} value={o.key}>{o.label}</option>
          ))}
        </select>
      </div>

      {error && (
        <div className="rounded-xl border border-red-500/40 bg-red-500/10 text-red-700 dark:text-red-300 px-4 py-3 text-sm">
          {error}
        </div>
      )}

      {loading ? (
        <p className="text-sm text-muted-foreground px-1">Cargando plantillas…</p>
      ) : visibles.length === 0 ? (
        <p data-testid={T.vacia} className="text-sm text-muted-foreground px-1 py-8 text-center">
          No hay plantillas para los filtros seleccionados.
        </p>
      ) : (
        <ul data-testid={T.lista} className="grid grid-cols-1 gap-3">
          {visibles.map((p) => (
            <li
              key={p.id}
              data-testid={T.item(p.id)}
              className="rounded-2xl border hairline bg-card p-4 md:p-5 shadow-soft flex flex-col md:flex-row md:items-center gap-4"
            >
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-medium truncate">{p.nombre}</span>
                  <span data-testid={T.itemEstado(p.id)}>
                    <EstadoBadge activa={p.activa} />
                  </span>
                </div>
                <div className="mt-1 flex flex-wrap gap-x-4 gap-y-1 text-xs text-muted-foreground">
                  <span>Salón: <b className="text-foreground">{p.salon}</b></span>
                  <span>Mesas: <b className="text-foreground">{p.totalMesas}</b></span>
                  <span>Capacidad: <b className="text-foreground">{p.capacidad}</b></span>
                </div>
              </div>
              <div className="flex items-center gap-2 flex-wrap">
                <button
                  type="button"
                  data-testid={T.btnEditar(p.id)}
                  onClick={() => navigate(`/admin/plantillas-mesas/${p.id}`)}
                  className="inline-flex items-center justify-center gap-1.5 rounded-full px-3 min-h-[44px] text-xs font-medium border hairline bg-card hover:bg-muted transition-colors"
                >
                  <Pencil className="w-3.5 h-3.5" strokeWidth={1.8} /> Ver / Editar
                </button>
                {p.activa ? (
                  <button
                    type="button"
                    data-testid={T.btnDesactivar(p.id)}
                    onClick={() => toggleActiva(p)}
                    className="inline-flex items-center justify-center gap-1.5 rounded-full px-3 min-h-[44px] text-xs font-medium border hairline bg-card hover:bg-muted transition-colors"
                  >
                    <Power className="w-3.5 h-3.5" strokeWidth={1.8} /> Desactivar
                  </button>
                ) : (
                  <button
                    type="button"
                    data-testid={T.btnActivar(p.id)}
                    onClick={() => toggleActiva(p)}
                    className="inline-flex items-center justify-center gap-1.5 rounded-full px-3 min-h-[44px] text-xs font-medium border hairline bg-card hover:bg-muted transition-colors"
                  >
                    <RotateCcw className="w-3.5 h-3.5" strokeWidth={1.8} /> Reactivar
                  </button>
                )}
                <button
                  type="button"
                  data-testid={T.btnEliminar(p.id)}
                  onClick={() => setPorEliminar(p)}
                  className="inline-flex items-center justify-center gap-1.5 rounded-full px-3 min-h-[44px] text-xs font-medium border border-red-500/40 text-red-700 dark:text-red-300 hover:bg-red-500/10 transition-colors"
                >
                  <Trash2 className="w-3.5 h-3.5" strokeWidth={1.8} /> Eliminar
                </button>
              </div>
            </li>
          ))}
        </ul>
      )}

      {porEliminar && (
        <div className="fixed inset-0 z-50 grid place-items-center bg-black/50 p-4">
          <div
            data-testid={T.confirmEliminar}
            role="alertdialog"
            aria-modal="true"
            className="w-full max-w-md rounded-2xl border hairline bg-card p-6 shadow-xl space-y-4"
          >
            <h2 className="text-lg font-semibold">Eliminar plantilla</h2>
            <p className="text-sm text-muted-foreground">
              La plantilla <b>{porEliminar.nombre}</b> quedará <b>inactiva</b> (baja lógica) y
              podrás consultarla en el filtro «Inactivas» o reactivarla después.
            </p>
            <div className="flex justify-end gap-3">
              <button
                type="button"
                data-testid={T.confirmEliminarCancel}
                onClick={() => setPorEliminar(null)}
                className="inline-flex items-center justify-center rounded-full px-4 min-h-[44px] text-sm font-medium border hairline bg-card hover:bg-muted"
              >
                Cancelar
              </button>
              <button
                type="button"
                data-testid={T.confirmEliminarOk}
                onClick={confirmarEliminar}
                className="inline-flex items-center justify-center rounded-full px-4 min-h-[44px] text-sm font-medium bg-red-600 text-white hover:bg-red-700"
              >
                Eliminar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
